import React, { useState, useEffect, useRef, useCallback } from 'react';
import { App as CapApp } from '@capacitor/app';
import type { Card, GameMode, BlitzEndReason, VersusTeam, VersusWinner } from './game/types';
import { generateDeck, shuffle, isSet, findSets, hasValidSet, findHint, findHintIndices } from './game/setEngine';
import { createPrng, generateRandomSeed } from './game/prng';
import { saveScore, saveBlitzScore } from './game/leaderboard';
import { vibrateBurst, vibrateSubtle, vibrateTick } from './utils/haptics';
import { MainMenu } from './components/MainMenu';
import { GameBar } from './components/GameBar';
import { CardTile } from './components/CardTile';
import { LeaderboardModal } from './components/LeaderboardModal';
import { GameOverModal } from './components/GameOverModal';
import { HelpModal } from './components/HelpModal';
import { VersusBuzzer, VersusBuzzerPair } from './components/VersusBuzzers';
import { OnlineLobby } from './components/OnlineLobby';
import { p2pClient } from './network/p2pClient';
import type { NetworkConnectionState, OnlineGameMode, PeerRole, NetworkMessage } from './network/types';
import {
  arbitrateLockConflict,
  getSymmetricScores,
  applyScoreDelta,
  checkClashGameOver,
} from './game/onlineClashLogic';

export const App: React.FC = () => {
  const [screen, setScreen] = useState<'menu' | 'game' | 'online_lobby'>('menu');
  const [gameMode, setGameMode] = useState<GameMode>('classic');
  const [deck, setDeck] = useState<Card[]>([]);
  const [board, setBoard] = useState<(Card | null)[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const [blitzTimeLeftMs, setBlitzTimeLeftMs] = useState<number>(30000);
  const [showBonusTime, setShowBonusTime] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isShimmering, setIsShimmering] = useState<boolean>(false);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState<boolean>(false);
  const [leaderboardInitialTab, setLeaderboardInitialTab] = useState<GameMode>('classic');
  const [lastBlitzEntryId, setLastBlitzEntryId] = useState<string | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState<boolean>(false);
  const [isAppActive, setIsAppActive] = useState<boolean>(() => {
    if (typeof document !== 'undefined') {
      return document.visibilityState === 'visible';
    }
    return true;
  });

  const isPaused = isHelpOpen || isLeaderboardOpen || !isAppActive;

  // Classic Game Over Stats
  const [gameOverStats, setGameOverStats] = useState<{
    finalTime: number;
    baseTime: number;
    penaltySeconds: number;
    hintsUsed: number;
    rank: number | null;
  } | null>(null);

  // Blitz Game Over Stats
  const [blitzGameOverStats, setBlitzGameOverStats] = useState<{
    setsFound: number;
    timeLeftMs: number;
    endReason: BlitzEndReason;
    rank: number | null;
  } | null>(null);

  // Versus Game Over Stats
  const [versusBlueScore, setVersusBlueScore] = useState<number>(0);
  const [versusRedScore, setVersusRedScore] = useState<number>(0);
  const [versusActiveTeam, setVersusActiveTeam] = useState<VersusTeam | null>(null);
  const [versusSelectionTimeLeftMs, setVersusSelectionTimeLeftMs] = useState<number>(5000);
  const [versusGameOverStats, setVersusGameOverStats] = useState<{
    blueScore: number;
    redScore: number;
    winner: VersusWinner;
    reason: string;
  } | null>(null);

  // Online Multiplayer State
  const [onlineLobbyMode, setOnlineLobbyMode] = useState<OnlineGameMode | null>(null);
  const [networkState, setNetworkState] = useState<NetworkConnectionState>('idle');
  const [onlineRoomCode, setOnlineRoomCode] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [onlineLockRole, setOnlineLockRole] = useState<PeerRole | null>(null);
  const [onlineSelectionTimeLeftMs, setOnlineSelectionTimeLeftMs] = useState<number>(5000);
  const [onlineScores, setOnlineScores] = useState<{ host: number; guest: number }>({ host: 0, guest: 0 });
  const [onlineOpponentProgress, setOnlineOpponentProgress] = useState<{ setsFound: number; cardsRemaining: number }>({
    setsFound: 0,
    cardsRemaining: 81,
  });
  const [onlineOpponentTimeLeftMs, setOnlineOpponentTimeLeftMs] = useState<number>(30000);
  const [onlineGameOverStats, setOnlineGameOverStats] = useState<{
    winner: 'self' | 'opponent' | 'tie';
    reason: string;
    myScore: number;
    opponentScore: number;
  } | null>(null);
  const [rematchRequestedByPeer, setRematchRequestedByPeer] = useState<boolean>(false);
  const [rematchSent, setRematchSent] = useState<boolean>(false);
  const [isReconnecting, setIsReconnecting] = useState<boolean>(false);
  const [reconnectingSeconds, setReconnectingSeconds] = useState<number>(15);

  const [isGameFinished, setIsGameFinished] = useState<boolean>(false);

  const [isPortrait, setIsPortrait] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerHeight >= window.innerWidth : true
  );

  // Seed & PRNG Refs
  const activeSeedRef = useRef<string | number>('');
  const prngRef = useRef<() => number>(Math.random);
  const initialUrlSeedCheckedRef = useRef<boolean>(false);

  // Board Ref for dev helpers
  const boardRef = useRef<(Card | null)[]>([]);
  boardRef.current = board;

  // Classic Game Refs
  const activeHintSetRef = useRef<Card[] | null>(null);
  const revealedHintCardIdsRef = useRef<Set<string>>(new Set());
  const hintsUsedRef = useRef<number>(0);
  const isProcessingSetRef = useRef<boolean>(false);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const pauseStartTimeRef = useRef<number | null>(null);
  const toastTimeoutRef = useRef<number | null>(null);

  // Blitz Game Refs
  const blitzEndTimeRef = useRef<number>(0);
  const pausedBlitzRemainingRef = useRef<number | null>(null);
  const blitzIntervalRef = useRef<number | null>(null);
  const blitzSetsFoundRef = useRef<number>(0);
  const bonusTimeoutRef = useRef<number | null>(null);
  const lastBlitzSecRef = useRef<number | null>(null);

  // Versus Game Refs
  const versusTimerRef = useRef<number | null>(null);
  const versusEndTimeRef = useRef<number>(0);
  const pausedVersusRemainingRef = useRef<number | null>(null);
  const versusBlueScoreRef = useRef<number>(0);
  const versusRedScoreRef = useRef<number>(0);
  const versusActiveTeamRef = useRef<VersusTeam | null>(null);
  const lastVersusSecRef = useRef<number | null>(null);

  // Online Multiplayer Refs
  const onlineLockRoleRef = useRef<PeerRole | null>(null);
  const onlineScoresRef = useRef<{ host: number; guest: number }>({ host: 0, guest: 0 });
  const onlineTimerRef = useRef<number | null>(null);
  const onlineEndTimeRef = useRef<number>(0);
  const lastOnlineTickSecRef = useRef<number | null>(null);
  const onlineSetsFoundRef = useRef<number>(0);
  const onlineOpponentProgressRef = useRef<{ setsFound: number; cardsRemaining: number }>({
    setsFound: 0,
    cardsRemaining: 81,
  });
  const reconnectIntervalRef = useRef<number | null>(null);

  // Resize listener for layout
  useEffect(() => {
    const handleResize = () => {
      setIsPortrait(window.innerHeight >= window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const triggerToast = useCallback((msg: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(msg);
    toastTimeoutRef.current = window.setTimeout(() => {
      setToastMessage(null);
    }, 1500);
  }, []);

  // Ensure board has at least 1 set without recursion or infinite loops
  const validateAndReshuffle = useCallback(
    (
      currentBoard: (Card | null)[],
      currentDeck: Card[],
      notify: boolean
    ): { board: (Card | null)[]; deck: Card[]; noSetsRemaining: boolean } => {
      const activeCards = currentBoard.filter((c): c is Card => c !== null);
      if (activeCards.length === 0) {
        return { board: currentBoard, deck: currentDeck, noSetsRemaining: true };
      }

      // If active cards on board already contain a valid set, no reshuffle needed
      if (hasValidSet(activeCards)) {
        return { board: currentBoard, deck: currentDeck, noSetsRemaining: false };
      }

      // Board has no valid set. Check all remaining cards (board + deck)
      const allCards = [...activeCards, ...currentDeck];
      const sets = findSets(allCards);

      if (sets.length === 0) {
        // No sets exist anywhere in remaining cards! Game over.
        return { board: currentBoard, deck: currentDeck, noSetsRemaining: true };
      }

      // Guarantee the board has at least one set deterministically using active PRNG.
      const rng = prngRef.current;
      const chosenSet = sets[Math.floor(rng() * sets.length)];
      const chosenIds = new Set(chosenSet.map((c) => c.id));
      const otherCards = shuffle(allCards.filter((c) => !chosenIds.has(c.id)), rng);

      const targetBoardSize = Math.min(9, allCards.length);
      const otherCardsForBoard = otherCards.splice(0, targetBoardSize - 3);
      const newDeck = otherCards;

      const boardCards = shuffle([...chosenSet, ...otherCardsForBoard], rng);
      const newBoard: (Card | null)[] = [
        ...boardCards,
        ...Array(9 - targetBoardSize).fill(null),
      ];

      activeHintSetRef.current = null;
      revealedHintCardIdsRef.current.clear();
      if (notify) {
        setIsShimmering(true);
        vibrateSubtle();
        window.setTimeout(() => setIsShimmering(false), 350);
      }

      return { board: newBoard, deck: newDeck, noSetsRemaining: false };
    },
    []
  );

  const handleBlitzGameOver = useCallback((reason: BlitzEndReason) => {
    if (blitzIntervalRef.current) {
      clearInterval(blitzIntervalRef.current);
      blitzIntervalRef.current = null;
    }
    lastBlitzSecRef.current = null;
    setIsGameFinished(true);

    const currentLeft = reason === 'timeout' ? 0 : Math.max(0, blitzEndTimeRef.current - Date.now());
    const sets = blitzSetsFoundRef.current;

    const res = saveBlitzScore(sets, currentLeft, reason);
    setLastBlitzEntryId(res.leaderboard.find((e) => e.rank === res.rank)?.id ?? null);
    setBlitzTimeLeftMs(currentLeft);
    setBlitzGameOverStats({
      setsFound: sets,
      timeLeftMs: currentLeft,
      endReason: reason,
      rank: res.rank,
    });
  }, []);

  const handleVersusGameOver = useCallback((winner: VersusWinner, reason: string) => {
    if (versusTimerRef.current) {
      clearInterval(versusTimerRef.current);
      versusTimerRef.current = null;
    }
    lastVersusSecRef.current = null;
    setIsGameFinished(true);
    setVersusActiveTeam(null);
    versusActiveTeamRef.current = null;
    setVersusGameOverStats({
      blueScore: versusBlueScoreRef.current,
      redScore: versusRedScoreRef.current,
      winner,
      reason,
    });
  }, []);

  const startClassicGame = useCallback(
    (customSeed?: string | number) => {
      let seed: string | number;
      if (typeof customSeed === 'string' && customSeed.trim() !== '') {
        seed = customSeed.trim();
      } else if (typeof customSeed === 'number' && !Number.isNaN(customSeed)) {
        seed = customSeed;
      } else {
        seed = generateRandomSeed();
      }

      activeSeedRef.current = seed;
      const rng = createPrng(seed);
      prngRef.current = rng;

      console.log(
        `%c[Find Sets!] 🎲 Classic Mode Seed: ${seed}`,
        'background: #7c3aed; color: #ffffff; font-weight: bold; padding: 3px 8px; border-radius: 3px; font-size: 11px;'
      );
      console.log(`[Find Sets!] Dev replay: window.startSeededGame("${seed}") or URL ?seed=${seed}`);

      const fullDeck = shuffle(generateDeck(), rng);
      const initialBoard = fullDeck.splice(0, 9);
      const { board: validatedBoard, deck: remainingDeck } = validateAndReshuffle(initialBoard, fullDeck, false);

      setGameMode('classic');
      setDeck(remainingDeck);
      setBoard(validatedBoard);
      setSelectedIndices([]);
      hintsUsedRef.current = 0;
      isProcessingSetRef.current = false;
      setElapsedMs(0);
      setGameOverStats(null);
      setBlitzGameOverStats(null);
      setVersusGameOverStats(null);
      setIsGameFinished(false);
      activeHintSetRef.current = null;
      revealedHintCardIdsRef.current.clear();

      startTimeRef.current = Date.now();
      pauseStartTimeRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      if (blitzIntervalRef.current) clearInterval(blitzIntervalRef.current);
      if (versusTimerRef.current) clearInterval(versusTimerRef.current);

      timerRef.current = window.setInterval(() => {
        setElapsedMs(Math.max(0, Date.now() - startTimeRef.current));
      }, 500);

      setScreen('game');
    },
    [validateAndReshuffle]
  );

  const startBlitzGame = useCallback(
    (customSeed?: string | number) => {
      let seed: string | number;
      if (typeof customSeed === 'string' && customSeed.trim() !== '') {
        seed = customSeed.trim();
      } else if (typeof customSeed === 'number' && !Number.isNaN(customSeed)) {
        seed = customSeed;
      } else {
        seed = generateRandomSeed();
      }

      activeSeedRef.current = seed;
      const rng = createPrng(seed);
      prngRef.current = rng;

      console.log(
        `%c[Find Sets!] ⚡ Blitz Mode Seed: ${seed}`,
        'background: #f59e0b; color: #ffffff; font-weight: bold; padding: 3px 8px; border-radius: 3px; font-size: 11px;'
      );

      const fullDeck = shuffle(generateDeck(), rng);
      const initialBoard = fullDeck.splice(0, 9);
      const { board: validatedBoard, deck: remainingDeck } = validateAndReshuffle(initialBoard, fullDeck, false);

      setGameMode('blitz');
      setDeck(remainingDeck);
      setBoard(validatedBoard);
      setSelectedIndices([]);
      hintsUsedRef.current = 0;
      isProcessingSetRef.current = false;
      setElapsedMs(0);
      setGameOverStats(null);
      setBlitzGameOverStats(null);
      setVersusGameOverStats(null);
      setIsGameFinished(false);
      activeHintSetRef.current = null;
      revealedHintCardIdsRef.current.clear();

      blitzSetsFoundRef.current = 0;
      setShowBonusTime(false);

      const initialMs = 30000;
      setBlitzTimeLeftMs(initialMs);
      blitzEndTimeRef.current = Date.now() + initialMs;
      pausedBlitzRemainingRef.current = null;

      if (timerRef.current) clearInterval(timerRef.current);
      if (blitzIntervalRef.current) clearInterval(blitzIntervalRef.current);
      if (versusTimerRef.current) clearInterval(versusTimerRef.current);

      lastBlitzSecRef.current = null;

      blitzIntervalRef.current = window.setInterval(() => {
        const remaining = Math.max(0, blitzEndTimeRef.current - Date.now());
        setBlitzTimeLeftMs(remaining);
        if (remaining <= 10000 && remaining > 0) {
          const sec = Math.ceil(remaining / 1000);
          if (sec !== lastBlitzSecRef.current) {
            lastBlitzSecRef.current = sec;
            vibrateTick();
          }
        } else if (remaining > 10000) {
          lastBlitzSecRef.current = null;
        }
        if (remaining <= 0) {
          lastBlitzSecRef.current = null;
          handleBlitzGameOver('timeout');
        }
      }, 100);

      setScreen('game');
    },
    [validateAndReshuffle, handleBlitzGameOver]
  );

  const startVersusGame = useCallback(
    (customSeed?: string | number) => {
      let seed: string | number;
      if (typeof customSeed === 'string' && customSeed.trim() !== '') {
        seed = customSeed.trim();
      } else if (typeof customSeed === 'number' && !Number.isNaN(customSeed)) {
        seed = customSeed;
      } else {
        seed = generateRandomSeed();
      }

      activeSeedRef.current = seed;
      const rng = createPrng(seed);
      prngRef.current = rng;

      console.log(
        `%c[Find Sets!] ⚔️ Versus Mode Seed: ${seed}`,
        'background: #2563eb; color: #ffffff; font-weight: bold; padding: 3px 8px; border-radius: 3px; font-size: 11px;'
      );

      const fullDeck = shuffle(generateDeck(), rng);
      const initialBoard = fullDeck.splice(0, 9);
      const { board: validatedBoard, deck: remainingDeck } = validateAndReshuffle(initialBoard, fullDeck, false);

      setGameMode('versus');
      setDeck(remainingDeck);
      setBoard(validatedBoard);
      setSelectedIndices([]);
      hintsUsedRef.current = 0;
      isProcessingSetRef.current = false;
      setElapsedMs(0);
      setGameOverStats(null);
      setBlitzGameOverStats(null);
      setVersusGameOverStats(null);
      setIsGameFinished(false);
      activeHintSetRef.current = null;
      revealedHintCardIdsRef.current.clear();

      setVersusBlueScore(0);
      versusBlueScoreRef.current = 0;
      setVersusRedScore(0);
      versusRedScoreRef.current = 0;
      setVersusActiveTeam(null);
      versusActiveTeamRef.current = null;
      setVersusSelectionTimeLeftMs(5000);
      pausedVersusRemainingRef.current = null;
      lastVersusSecRef.current = null;

      if (timerRef.current) clearInterval(timerRef.current);
      if (blitzIntervalRef.current) clearInterval(blitzIntervalRef.current);
      if (versusTimerRef.current) clearInterval(versusTimerRef.current);

      setScreen('game');
    },
    [validateAndReshuffle]
  );

  const handleBuzzerPress = useCallback(
    (team: VersusTeam) => {
      if (
        gameMode !== 'versus' ||
        versusActiveTeamRef.current !== null ||
        isProcessingSetRef.current ||
        isGameFinished ||
        isPaused
      ) {
        return;
      }
      setVersusActiveTeam(team);
      versusActiveTeamRef.current = team;
      setSelectedIndices([]);
      vibrateSubtle();

      const durationMs = 5000;
      setVersusSelectionTimeLeftMs(durationMs);
      versusEndTimeRef.current = Date.now() + durationMs;
      pausedVersusRemainingRef.current = null;
      lastVersusSecRef.current = 5;

      if (versusTimerRef.current) clearInterval(versusTimerRef.current);
      versusTimerRef.current = window.setInterval(() => {
        const remaining = Math.max(0, versusEndTimeRef.current - Date.now());
        setVersusSelectionTimeLeftMs(remaining);
        if (remaining > 0) {
          const sec = Math.ceil(remaining / 1000);
          if (sec !== lastVersusSecRef.current) {
            lastVersusSecRef.current = sec;
            vibrateTick();
          }
        }
        if (remaining <= 0) {
          lastVersusSecRef.current = null;
          if (versusTimerRef.current) {
            clearInterval(versusTimerRef.current);
            versusTimerRef.current = null;
          }
          const currentTeam = versusActiveTeamRef.current;
          if (currentTeam === 'blue') {
            versusBlueScoreRef.current = Math.max(0, versusBlueScoreRef.current - 1);
            setVersusBlueScore(versusBlueScoreRef.current);
          } else if (currentTeam === 'red') {
            versusRedScoreRef.current = Math.max(0, versusRedScoreRef.current - 1);
            setVersusRedScore(versusRedScoreRef.current);
          }
          triggerToast(`${currentTeam === 'blue' ? 'Blue' : 'Red'} timeout! (-1 point)`);
          vibrateBurst();
          setSelectedIndices([]);
          setVersusActiveTeam(null);
          versusActiveTeamRef.current = null;
          setVersusSelectionTimeLeftMs(5000);
        }
      }, 100);
    },
    [gameMode, isGameFinished, isPaused, triggerToast]
  );

  const handleOnlineClashGameOver = useCallback(
    (winnerRole: PeerRole | 'tie' | null, finalScores: { host: number; guest: number }) => {
      setIsGameFinished(true);
      if (onlineTimerRef.current) {
        clearInterval(onlineTimerRef.current);
        onlineTimerRef.current = null;
      }
      const myRole = p2pClient.getRole();
      const winner: 'self' | 'opponent' | 'tie' =
        winnerRole === 'tie' ? 'tie' : winnerRole === myRole ? 'self' : 'opponent';
      const { blueScore, redScore } = getSymmetricScores(finalScores, myRole || 'host');

      setOnlineGameOverStats({
        winner,
        reason:
          winner === 'tie'
            ? 'Equal sets found!'
            : winner === 'self'
            ? 'You won the match!'
            : 'Opponent won the match!',
        myScore: blueScore,
        opponentScore: redScore,
      });
    },
    []
  );

  const startOnlineClashGame = useCallback(
    (customSeed?: string | number) => {
      let seed: string | number;
      if (typeof customSeed === 'string' && customSeed.trim() !== '') {
        seed = customSeed.trim();
      } else if (typeof customSeed === 'number' && !Number.isNaN(customSeed)) {
        seed = customSeed;
      } else {
        seed = generateRandomSeed();
      }
      activeSeedRef.current = seed;
      const rng = createPrng(seed);
      prngRef.current = rng;

      const fullDeck = shuffle(generateDeck(), rng);
      const initialBoard = fullDeck.splice(0, 9);
      const { board: validatedBoard, deck: remainingDeck } = validateAndReshuffle(initialBoard, fullDeck, false);

      setGameMode('online_clash');
      setDeck(remainingDeck);
      setBoard(validatedBoard);
      setSelectedIndices([]);
      hintsUsedRef.current = 0;
      isProcessingSetRef.current = false;
      setElapsedMs(0);
      setGameOverStats(null);
      setBlitzGameOverStats(null);
      setVersusGameOverStats(null);
      setOnlineGameOverStats(null);
      setIsGameFinished(false);
      setRematchRequestedByPeer(false);
      setRematchSent(false);

      const initialScores = { host: 0, guest: 0 };
      setOnlineScores(initialScores);
      onlineScoresRef.current = initialScores;
      setOnlineLockRole(null);
      onlineLockRoleRef.current = null;
      setOnlineSelectionTimeLeftMs(5000);

      if (timerRef.current) clearInterval(timerRef.current);
      if (blitzIntervalRef.current) clearInterval(blitzIntervalRef.current);
      if (versusTimerRef.current) clearInterval(versusTimerRef.current);
      if (onlineTimerRef.current) clearInterval(onlineTimerRef.current);

      setScreen('game');
    },
    [validateAndReshuffle]
  );

  const startOnlineRaceGame = useCallback(
    (customSeed?: string | number) => {
      let seed: string | number;
      if (typeof customSeed === 'string' && customSeed.trim() !== '') {
        seed = customSeed.trim();
      } else if (typeof customSeed === 'number' && !Number.isNaN(customSeed)) {
        seed = customSeed;
      } else {
        seed = generateRandomSeed();
      }
      activeSeedRef.current = seed;
      const rng = createPrng(seed);
      prngRef.current = rng;

      const fullDeck = shuffle(generateDeck(), rng);
      const initialBoard = fullDeck.splice(0, 9);
      const { board: validatedBoard, deck: remainingDeck } = validateAndReshuffle(initialBoard, fullDeck, false);

      setGameMode('online_race');
      setDeck(remainingDeck);
      setBoard(validatedBoard);
      setSelectedIndices([]);
      hintsUsedRef.current = 0;
      isProcessingSetRef.current = false;
      setElapsedMs(0);
      setGameOverStats(null);
      setBlitzGameOverStats(null);
      setVersusGameOverStats(null);
      setOnlineGameOverStats(null);
      setIsGameFinished(false);
      setRematchRequestedByPeer(false);
      setRematchSent(false);

      onlineSetsFoundRef.current = 0;
      onlineOpponentProgressRef.current = { setsFound: 0, cardsRemaining: remainingDeck.length };
      setOnlineOpponentProgress({ setsFound: 0, cardsRemaining: remainingDeck.length });

      if (timerRef.current) clearInterval(timerRef.current);
      if (blitzIntervalRef.current) clearInterval(blitzIntervalRef.current);
      if (versusTimerRef.current) clearInterval(versusTimerRef.current);
      if (onlineTimerRef.current) clearInterval(onlineTimerRef.current);

      startTimeRef.current = Date.now();
      timerRef.current = window.setInterval(() => {
        setElapsedMs(Math.max(0, Date.now() - startTimeRef.current));
      }, 500);

      setScreen('game');
    },
    [validateAndReshuffle]
  );

  const startOnlineBlitzGame = useCallback(
    (customSeed?: string | number) => {
      let seed: string | number;
      if (typeof customSeed === 'string' && customSeed.trim() !== '') {
        seed = customSeed.trim();
      } else if (typeof customSeed === 'number' && !Number.isNaN(customSeed)) {
        seed = customSeed;
      } else {
        seed = generateRandomSeed();
      }
      activeSeedRef.current = seed;
      const rng = createPrng(seed);
      prngRef.current = rng;

      const fullDeck = shuffle(generateDeck(), rng);
      const initialBoard = fullDeck.splice(0, 9);
      const { board: validatedBoard, deck: remainingDeck } = validateAndReshuffle(initialBoard, fullDeck, false);

      setGameMode('online_blitz');
      setDeck(remainingDeck);
      setBoard(validatedBoard);
      setSelectedIndices([]);
      hintsUsedRef.current = 0;
      isProcessingSetRef.current = false;
      setElapsedMs(0);
      setGameOverStats(null);
      setBlitzGameOverStats(null);
      setVersusGameOverStats(null);
      setOnlineGameOverStats(null);
      setIsGameFinished(false);
      setRematchRequestedByPeer(false);
      setRematchSent(false);

      onlineSetsFoundRef.current = 0;
      onlineOpponentProgressRef.current = { setsFound: 0, cardsRemaining: remainingDeck.length };
      setOnlineOpponentProgress({ setsFound: 0, cardsRemaining: remainingDeck.length });
      setOnlineOpponentTimeLeftMs(30000);

      const initialDurationMs = 30000;
      setBlitzTimeLeftMs(initialDurationMs);
      blitzEndTimeRef.current = Date.now() + initialDurationMs;
      pausedBlitzRemainingRef.current = null;
      lastBlitzSecRef.current = null;

      if (timerRef.current) clearInterval(timerRef.current);
      if (blitzIntervalRef.current) clearInterval(blitzIntervalRef.current);
      if (versusTimerRef.current) clearInterval(versusTimerRef.current);
      if (onlineTimerRef.current) clearInterval(onlineTimerRef.current);

      blitzIntervalRef.current = window.setInterval(() => {
        const remaining = Math.max(0, blitzEndTimeRef.current - Date.now());
        setBlitzTimeLeftMs(remaining);

        if (remaining <= 10000 && remaining > 0) {
          const sec = Math.ceil(remaining / 1000);
          if (sec !== lastBlitzSecRef.current) {
            lastBlitzSecRef.current = sec;
            vibrateTick();
          }
        } else if (remaining > 10000) {
          lastBlitzSecRef.current = null;
        }

        if (remaining <= 0) {
          lastBlitzSecRef.current = null;
          if (blitzIntervalRef.current) {
            clearInterval(blitzIntervalRef.current);
            blitzIntervalRef.current = null;
          }
          vibrateBurst();
          setIsGameFinished(true);
          p2pClient.send({
            type: 'BLITZ_ELIMINATED',
            reason: 'timeout',
            setsFound: onlineSetsFoundRef.current,
          });
          setOnlineGameOverStats({
            winner: 'opponent',
            reason: 'Time ran out! Knocked out!',
            myScore: onlineSetsFoundRef.current,
            opponentScore: onlineOpponentProgressRef.current.setsFound,
          });
        }
      }, 100);

      setScreen('game');
    },
    [validateAndReshuffle]
  );

  const startOnlineSelectionTimer = useCallback(
    (lockingRole: PeerRole) => {
      if (onlineTimerRef.current) clearInterval(onlineTimerRef.current);
      const durationMs = 5000;
      setOnlineSelectionTimeLeftMs(durationMs);
      onlineEndTimeRef.current = Date.now() + durationMs;
      lastOnlineTickSecRef.current = 5;

      const myRole = p2pClient.getRole();
      const isMe = lockingRole === myRole;

      // Only locking player feels ticks!
      if (isMe) {
        vibrateTick();
      }

      onlineTimerRef.current = window.setInterval(() => {
        const remaining = Math.max(0, onlineEndTimeRef.current - Date.now());
        setOnlineSelectionTimeLeftMs(remaining);

        if (remaining > 0) {
          const sec = Math.ceil(remaining / 1000);
          if (sec !== lastOnlineTickSecRef.current) {
            lastOnlineTickSecRef.current = sec;
            if (isMe) {
              vibrateTick();
            }
          }
        }

        if (remaining <= 0) {
          lastOnlineTickSecRef.current = null;
          if (onlineTimerRef.current) {
            clearInterval(onlineTimerRef.current);
            onlineTimerRef.current = null;
          }

          if (isMe) {
            vibrateBurst();
            triggerToast('Selection timeout! (-1 point)');
            const newScores = applyScoreDelta(onlineScoresRef.current, lockingRole, -1);
            onlineScoresRef.current = newScores;
            setOnlineScores(newScores);
            setSelectedIndices([]);
            setOnlineLockRole(null);
            onlineLockRoleRef.current = null;
            p2pClient.send({
              type: 'CLASH_TIMEOUT',
              penalizedRole: lockingRole,
              scores: newScores,
            });
          }
        }
      }, 100);
    },
    [triggerToast]
  );

  const resolveOnlineClashValidSet = useCallback(
    (matchedIndices: number[], solverRole: PeerRole) => {
      const nextBoard = [...board];
      const nextDeck = [...deck];

      if (nextDeck.length >= 3) {
        const replacements = nextDeck.splice(0, 3);
        nextBoard[matchedIndices[0]] = replacements[0];
        nextBoard[matchedIndices[1]] = replacements[1];
        nextBoard[matchedIndices[2]] = replacements[2];
      } else {
        nextBoard[matchedIndices[0]] = null;
        nextBoard[matchedIndices[1]] = null;
        nextBoard[matchedIndices[2]] = null;
      }

      const { board: finalBoard, deck: finalDeck } = validateAndReshuffle(nextBoard, nextDeck, true);
      const newScores = applyScoreDelta(onlineScoresRef.current, solverRole, 1);
      onlineScoresRef.current = newScores;
      setOnlineScores(newScores);
      setBoard(finalBoard);
      setDeck(finalDeck);
      setSelectedIndices([]);
      setOnlineLockRole(null);
      onlineLockRoleRef.current = null;

      const remainingCards = finalBoard.filter((c): c is Card => c !== null);
      const gameOver = checkClashGameOver(newScores, remainingCards, finalDeck);

      p2pClient.send({
        type: 'CLASH_SET_RESOLVED',
        valid: true,
        solvedBy: solverRole,
        selectedCardIds: matchedIndices.map((i) => board[i]!.id),
        newBoard: finalBoard as Card[],
        newDeck: finalDeck,
        scores: newScores,
        isGameOver: gameOver.isOver,
      });

      if (gameOver.isOver) {
        handleOnlineClashGameOver(gameOver.winnerRole, newScores);
      }
    },
    [board, deck, validateAndReshuffle, handleOnlineClashGameOver]
  );

  const handleCreateOnlineRoom = async () => {
    if (!onlineLobbyMode) return;
    try {
      setNetworkError(null);
      const code = await p2pClient.createRoom(onlineLobbyMode);
      setOnlineRoomCode(code);
    } catch (err: any) {
      setNetworkError(err?.message || 'Failed to create room');
    }
  };

  const handleJoinOnlineRoom = async (code: string) => {
    if (!onlineLobbyMode) return;
    try {
      setNetworkError(null);
      await p2pClient.joinRoom(onlineLobbyMode, code);
      setOnlineRoomCode(code);
    } catch (err: any) {
      setNetworkError(err?.message || 'Failed to join room');
    }
  };

  const handleCancelOnlineLobby = () => {
    p2pClient.cleanup();
    setOnlineLobbyMode(null);
    setOnlineRoomCode(null);
    setNetworkError(null);
    setScreen('menu');
  };

  const handleRematchClick = () => {
    if (rematchRequestedByPeer) {
      const newSeed = generateRandomSeed();
      p2pClient.send({ type: 'REMATCH_ACCEPT', newSeed });
      if (gameMode === 'online_clash') {
        startOnlineClashGame(newSeed);
      } else if (gameMode === 'online_blitz') {
        startOnlineBlitzGame(newSeed);
      } else {
        startOnlineRaceGame(newSeed);
      }
      setRematchRequestedByPeer(false);
      setRematchSent(false);
    } else {
      p2pClient.send({ type: 'REMATCH_REQUEST' });
      setRematchSent(true);
    }
  };

  // Expose developer helpers on window and auto-start if URL has ?seed=...
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).startSeededGame = (seed: string | number) => {
        startClassicGame(seed);
      };
      (window as any).startBlitzGame = (seed: string | number) => {
        startBlitzGame(seed);
      };
      (window as any).startVersusGame = (seed?: string | number) => {
        startVersusGame(seed);
      };
      (window as any).getGameSeed = () => activeSeedRef.current;
      (window as any).__SET_DEV__ = {
        startSeededGame: (seed: string | number) => startClassicGame(seed),
        startBlitzGame: (seed: string | number) => startBlitzGame(seed),
        startVersusGame: (seed?: string | number) => startVersusGame(seed),
        startOnlineClashGame: (seed: string | number) => startOnlineClashGame(seed),
        startOnlineRaceGame: (seed: string | number) => startOnlineRaceGame(seed),
        startOnlineBlitzGame: (seed: string | number) => startOnlineBlitzGame(seed),
        buzzTeam: (team: VersusTeam) => handleBuzzerPress(team),
        getSeed: () => activeSeedRef.current,
        triggerGameOver: (stats?: any) => {
          const baseTime = stats?.baseTime ?? 127000;
          const totalHints = stats?.hintsUsed ?? 1;
          const penaltySeconds = totalHints * 15;
          const finalTime = baseTime + penaltySeconds * 1000;
          const entry = saveScore(baseTime, totalHints);
          setIsGameFinished(true);
          setGameOverStats({
            finalTime,
            baseTime,
            penaltySeconds,
            hintsUsed: totalHints,
            rank: entry ? entry.rank : 1,
          });
        },
        triggerBlitzGameOver: (stats?: any) => {
          const sets = stats?.setsFound ?? 8;
          const timeLeft = stats?.timeLeftMs ?? 12000;
          const endReason = stats?.endReason ?? 'timeout';
          const res = saveBlitzScore(sets, timeLeft, endReason);
          setIsGameFinished(true);
          setLastBlitzEntryId(res.leaderboard.find((e) => e.rank === res.rank)?.id ?? null);
          setBlitzGameOverStats({
            setsFound: sets,
            timeLeftMs: timeLeft,
            endReason,
            rank: res.rank,
          });
        },
        triggerVersusGameOver: (stats?: any) => {
          const blue = stats?.blueScore ?? 14;
          const red = stats?.redScore ?? 10;
          const winner = stats?.winner ?? (blue > red ? 'blue' : red > blue ? 'red' : 'tie');
          const reason = stats?.reason ?? 'First to 14 sets!';
          versusBlueScoreRef.current = blue;
          versusRedScoreRef.current = red;
          setVersusBlueScore(blue);
          setVersusRedScore(red);
          handleVersusGameOver(winner, reason);
        },
        triggerToast: (msg: string) => triggerToast(msg),
        triggerShimmer: () => {
          setIsShimmering(true);
          setTimeout(() => setIsShimmering(false), 800);
        },
        getHintIndices: () => findHintIndices(boardRef.current),
        getHintCards: () => findHint(boardRef.current),
        setAppActive: (active: boolean) => setIsAppActive(active),
        isAppActive: () => isAppActive,
      };

      if (!initialUrlSeedCheckedRef.current) {
        initialUrlSeedCheckedRef.current = true;
        const params = new URLSearchParams(window.location.search);
        const urlSeed = params.get('seed');
        if (urlSeed && urlSeed.trim() !== '') {
          startClassicGame(urlSeed.trim());
          try {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('seed');
            window.history.replaceState({}, '', newUrl.pathname + (newUrl.search ? newUrl.search : '') + newUrl.hash);
          } catch {
            // ignore in non-browser/test envs
          }
        }
      }
    }
  }, [startClassicGame, startBlitzGame, startVersusGame, startOnlineClashGame, startOnlineRaceGame, startOnlineBlitzGame, handleBuzzerPress, handleVersusGameOver, triggerToast, isAppActive]);

  // WebRTC P2P Network Message & Lifecycle Listener
  useEffect(() => {
    const unsubState = p2pClient.onStateChange((state, error) => {
      setNetworkState(state);
      if (error) setNetworkError(error);

      if (state === 'connected') {
        setNetworkError(null);
        setIsReconnecting(false);
        if (p2pClient.getRole() === 'host' && onlineLobbyMode) {
          const seed = generateRandomSeed();
          const startMsg = { mode: onlineLobbyMode, seed };
          p2pClient.setLastStartGameMsg(startMsg);
          p2pClient.send({ type: 'START_GAME', ...startMsg });
          if (onlineLobbyMode === 'clash') {
            startOnlineClashGame(seed);
          } else if (onlineLobbyMode === 'blitz') {
            startOnlineBlitzGame(seed);
          } else {
            startOnlineRaceGame(seed);
          }

          // Backup delayed re-send in case guest data channel was still settling
          window.setTimeout(() => {
            if (p2pClient.getState() === 'connected') {
              p2pClient.send({ type: 'START_GAME', ...startMsg });
            }
          }, 300);
        }
      }

      if (state === 'reconnecting') {
        setIsReconnecting(true);
        setReconnectingSeconds(15);
        if (reconnectIntervalRef.current) clearInterval(reconnectIntervalRef.current);
        let secs = 15;
        reconnectIntervalRef.current = window.setInterval(() => {
          secs -= 1;
          setReconnectingSeconds(secs);
          if (secs <= 0 && reconnectIntervalRef.current) {
            clearInterval(reconnectIntervalRef.current);
          }
        }, 1000);
      } else {
        setIsReconnecting(false);
        if (reconnectIntervalRef.current) {
          clearInterval(reconnectIntervalRef.current);
          reconnectIntervalRef.current = null;
        }
      }

      if (state === 'disconnected') {
        if (screen === 'game' && !isGameFinished) {
          setIsGameFinished(true);
          if (onlineTimerRef.current) clearInterval(onlineTimerRef.current);
          if (timerRef.current) clearInterval(timerRef.current);
          if (blitzIntervalRef.current) {
            clearInterval(blitzIntervalRef.current);
            blitzIntervalRef.current = null;
          }
          const myRole = p2pClient.getRole() || 'host';
          const { blueScore, redScore } = getSymmetricScores(onlineScoresRef.current, myRole);
          setOnlineGameOverStats({
            winner: 'self',
            reason: error || 'Opponent left the match (Victory by forfeit!)',
            myScore: gameMode === 'online_race' ? onlineSetsFoundRef.current : blueScore,
            opponentScore: gameMode === 'online_race' ? onlineOpponentProgressRef.current.setsFound : redScore,
          });
          triggerToast('Opponent left the match.');
        }
      }
    });

    const unsubMsg = p2pClient.onMessage((msg: NetworkMessage) => {
      const myRole = p2pClient.getRole();

      if (msg.type === 'START_GAME') {
        // Prevent duplicate re-initialization if already in game with this exact seed
        if (screen === 'game' && activeSeedRef.current === msg.seed) {
          return;
        }
        if (msg.mode === 'clash') {
          startOnlineClashGame(msg.seed);
        } else if (msg.mode === 'blitz') {
          startOnlineBlitzGame(msg.seed);
        } else {
          startOnlineRaceGame(msg.seed);
        }
      } else if (msg.type === 'CLASH_LOCK_REQUEST') {
        // Host arbitrates incoming guest lock request
        if (myRole === 'host') {
          if (onlineLockRoleRef.current === null) {
            // Board is free -> grant to guest
            onlineLockRoleRef.current = 'guest';
            setOnlineLockRole('guest');
            setSelectedIndices([msg.cardIndex]);
            startOnlineSelectionTimer('guest');
            p2pClient.send({
              type: 'CLASH_LOCK_ACQUIRED',
              lockedBy: 'guest',
              cardIndex: msg.cardIndex,
            });
          } else if (onlineLockRoleRef.current === 'host' && onlineLockTimestampRef.current !== null) {
            // Near-simultaneous conflict!
            const winner = arbitrateLockConflict(onlineLockTimestampRef.current, msg.timestamp);
            if (winner === 'guest') {
              // Yield lock to guest
              onlineLockRoleRef.current = 'guest';
              setOnlineLockRole('guest');
              setSelectedIndices([msg.cardIndex]);
              startOnlineSelectionTimer('guest');
            }
            p2pClient.send({
              type: 'CLASH_LOCK_ACQUIRED',
              lockedBy: winner,
              cardIndex: winner === 'guest' ? msg.cardIndex : (selectedIndices[0] ?? msg.cardIndex),
            });
          }
        }
      } else if (msg.type === 'CLASH_LOCK_ACQUIRED') {
        onlineLockRoleRef.current = msg.lockedBy;
        setOnlineLockRole(msg.lockedBy);
        setSelectedIndices([msg.cardIndex]);
        startOnlineSelectionTimer(msg.lockedBy);
        if (msg.lockedBy !== myRole) {
          // Locked out by peer
          triggerToast('Opponent locked board!');
        }
      } else if (msg.type === 'CLASH_CARD_SELECT') {
        setSelectedIndices((prev) => {
          const next = prev.includes(msg.cardIndex) ? prev : [...prev, msg.cardIndex];
          // If Host and peer selected 3 cards, Host evaluates and resolves
          if (myRole === 'host' && onlineLockRoleRef.current === 'guest' && next.length === 3) {
            const c1 = board[next[0]];
            const c2 = board[next[1]];
            const c3 = board[next[2]];
            if (c1 && c2 && c3 && isSet(c1, c2, c3)) {
              if (onlineTimerRef.current) {
                clearInterval(onlineTimerRef.current);
                onlineTimerRef.current = null;
              }
              resolveOnlineClashValidSet(next, 'guest');
            } else {
              if (onlineTimerRef.current) {
                clearInterval(onlineTimerRef.current);
                onlineTimerRef.current = null;
              }
              const newScores = applyScoreDelta(onlineScoresRef.current, 'guest', -1);
              onlineScoresRef.current = newScores;
              setOnlineScores(newScores);
              setSelectedIndices([]);
              setOnlineLockRole(null);
              onlineLockRoleRef.current = null;
              p2pClient.send({
                type: 'CLASH_TIMEOUT',
                penalizedRole: 'guest',
                scores: newScores,
              });
              triggerToast('Opponent invalid set! (-1 point)');
            }
          }
          return next;
        });
      } else if (msg.type === 'CLASH_CARD_DESELECT') {
        setSelectedIndices((prev) => prev.filter((idx) => idx !== msg.cardIndex));
      } else if (msg.type === 'CLASH_SET_RESOLVED') {
        if (onlineTimerRef.current) {
          clearInterval(onlineTimerRef.current);
          onlineTimerRef.current = null;
        }
        onlineScoresRef.current = msg.scores;
        setOnlineScores(msg.scores);
        setBoard(msg.newBoard);
        setDeck(msg.newDeck);
        setSelectedIndices([]);
        setOnlineLockRole(null);
        onlineLockRoleRef.current = null;

        if (msg.solvedBy === myRole) {
          vibrateSubtle();
          triggerToast('Set found! (+1 point)');
        } else {
          triggerToast('Opponent found a set!');
        }

        if (msg.isGameOver) {
          const winnerRole =
            msg.scores.host > msg.scores.guest
              ? 'host'
              : msg.scores.guest > msg.scores.host
              ? 'guest'
              : 'tie';
          handleOnlineClashGameOver(winnerRole, msg.scores);
        }
      } else if (msg.type === 'CLASH_TIMEOUT') {
        if (onlineTimerRef.current) {
          clearInterval(onlineTimerRef.current);
          onlineTimerRef.current = null;
        }
        onlineScoresRef.current = msg.scores;
        setOnlineScores(msg.scores);
        setSelectedIndices([]);
        setOnlineLockRole(null);
        onlineLockRoleRef.current = null;
        const wasMe = msg.penalizedRole === myRole;
        triggerToast(`${wasMe ? 'Your' : 'Opponent'} timeout/error! (-1 point)`);
      } else if (msg.type === 'RACE_PROGRESS') {
        vibrateSubtle();
        onlineOpponentProgressRef.current = {
          setsFound: msg.setsFound,
          cardsRemaining: msg.cardsRemaining,
        };
        setOnlineOpponentProgress({
          setsFound: msg.setsFound,
          cardsRemaining: msg.cardsRemaining,
        });
      } else if (msg.type === 'RACE_FINISHED') {
        setIsGameFinished(true);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setOnlineGameOverStats({
          winner: 'opponent',
          reason: 'Opponent finished all sets first!',
          myScore: onlineSetsFoundRef.current,
          opponentScore: msg.setsFound,
        });
      } else if (msg.type === 'BLITZ_PROGRESS') {
        vibrateSubtle();
        onlineOpponentProgressRef.current = {
          setsFound: msg.setsFound,
          cardsRemaining: msg.cardsRemaining,
        };
        setOnlineOpponentProgress({
          setsFound: msg.setsFound,
          cardsRemaining: msg.cardsRemaining,
        });
        setOnlineOpponentTimeLeftMs(msg.timeLeftMs);
      } else if (msg.type === 'BLITZ_ELIMINATED') {
        if (blitzIntervalRef.current) {
          clearInterval(blitzIntervalRef.current);
          blitzIntervalRef.current = null;
        }
        setIsGameFinished(true);
        const reasonText =
          msg.reason === 'timeout'
            ? 'Opponent ran out of time! Victory!'
            : 'Opponent made an invalid set! Victory!';
        setOnlineGameOverStats({
          winner: 'self',
          reason: reasonText,
          myScore: onlineSetsFoundRef.current,
          opponentScore: msg.setsFound,
        });
      } else if (msg.type === 'BLITZ_DECK_CLEARED') {
        if (blitzIntervalRef.current) {
          clearInterval(blitzIntervalRef.current);
          blitzIntervalRef.current = null;
        }
        setIsGameFinished(true);
        setOnlineGameOverStats({
          winner: 'opponent',
          reason: 'Opponent cleared the entire deck first!',
          myScore: onlineSetsFoundRef.current,
          opponentScore: msg.setsFound,
        });
      } else if (msg.type === 'REMATCH_REQUEST') {
        setRematchRequestedByPeer(true);
        triggerToast('Opponent requested a rematch!');
      } else if (msg.type === 'REMATCH_ACCEPT') {
        setRematchRequestedByPeer(false);
        setRematchSent(false);
        if (gameMode === 'online_clash') {
          startOnlineClashGame(msg.newSeed);
        } else if (gameMode === 'online_blitz') {
          startOnlineBlitzGame(msg.newSeed);
        } else {
          startOnlineRaceGame(msg.newSeed);
        }
      } else if (msg.type === 'LEAVE_MATCH' || msg.type === 'OPPONENT_DISCONNECTED') {
        triggerToast('Opponent left the match.');
        if (screen === 'game' && !isGameFinished) {
          setIsGameFinished(true);
          if (blitzIntervalRef.current) {
            clearInterval(blitzIntervalRef.current);
            blitzIntervalRef.current = null;
          }
          if (onlineTimerRef.current) {
            clearInterval(onlineTimerRef.current);
            onlineTimerRef.current = null;
          }
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          const { blueScore, redScore } = getSymmetricScores(onlineScoresRef.current, myRole || 'host');
          setOnlineGameOverStats({
            winner: 'self',
            reason: 'Opponent left the match (Victory by forfeit!)',
            myScore: gameMode === 'online_race' ? onlineSetsFoundRef.current : blueScore,
            opponentScore: gameMode === 'online_race' ? onlineOpponentProgressRef.current.setsFound : redScore,
          });
        }
      }
    });

    return () => {
      unsubState();
      unsubMsg();
    };
  }, [
    screen,
    isGameFinished,
    onlineLobbyMode,
    gameMode,
    board,
    startOnlineClashGame,
    startOnlineRaceGame,
    startOnlineBlitzGame,
    startOnlineSelectionTimer,
    resolveOnlineClashValidSet,
    handleOnlineClashGameOver,
    triggerToast,
  ]);

  const pauseTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (screen === 'game' && !gameOverStats && startTimeRef.current > 0) {
      if (pauseStartTimeRef.current === null) {
        pauseStartTimeRef.current = Date.now();
      }
    }
  }, [screen, gameOverStats]);

  const resumeTimer = useCallback(() => {
    if (screen === 'game' && !gameOverStats) {
      if (pauseStartTimeRef.current !== null && startTimeRef.current > 0) {
        const pausedDuration = Math.max(0, Date.now() - pauseStartTimeRef.current);
        startTimeRef.current += pausedDuration;
      }
      pauseStartTimeRef.current = null;

      if (!timerRef.current && startTimeRef.current > 0) {
        setElapsedMs(Math.max(0, Date.now() - startTimeRef.current));
        timerRef.current = window.setInterval(() => {
          setElapsedMs(Math.max(0, Date.now() - startTimeRef.current));
        }, 500);
      }
    }
  }, [screen, gameOverStats]);

  const pauseBlitzTimer = useCallback(() => {
    if (blitzIntervalRef.current) {
      clearInterval(blitzIntervalRef.current);
      blitzIntervalRef.current = null;
    }
    if (screen === 'game' && !blitzGameOverStats && blitzEndTimeRef.current > 0) {
      if (pausedBlitzRemainingRef.current === null) {
        pausedBlitzRemainingRef.current = Math.max(0, blitzEndTimeRef.current - Date.now());
      }
    }
  }, [screen, blitzGameOverStats]);

  const resumeBlitzTimer = useCallback(() => {
    if (screen === 'game' && !blitzGameOverStats) {
      if (pausedBlitzRemainingRef.current !== null) {
        blitzEndTimeRef.current = Date.now() + pausedBlitzRemainingRef.current;
        pausedBlitzRemainingRef.current = null;
      }
      if (!blitzIntervalRef.current && blitzEndTimeRef.current > Date.now()) {
        const remaining = Math.max(0, blitzEndTimeRef.current - Date.now());
        setBlitzTimeLeftMs(remaining);
        blitzIntervalRef.current = window.setInterval(() => {
          const currentRemaining = Math.max(0, blitzEndTimeRef.current - Date.now());
          setBlitzTimeLeftMs(currentRemaining);
          if (currentRemaining <= 10000 && currentRemaining > 0) {
            const sec = Math.ceil(currentRemaining / 1000);
            if (sec !== lastBlitzSecRef.current) {
              lastBlitzSecRef.current = sec;
              vibrateTick();
            }
          } else if (currentRemaining > 10000) {
            lastBlitzSecRef.current = null;
          }
          if (currentRemaining <= 0) {
            lastBlitzSecRef.current = null;
            handleBlitzGameOver('timeout');
          }
        }, 100);
      }
    }
  }, [screen, blitzGameOverStats, handleBlitzGameOver]);

  const pauseVersusTimer = useCallback(() => {
    if (versusTimerRef.current) {
      clearInterval(versusTimerRef.current);
      versusTimerRef.current = null;
    }
    if (
      screen === 'game' &&
      !versusGameOverStats &&
      versusActiveTeamRef.current !== null &&
      versusEndTimeRef.current > 0
    ) {
      if (pausedVersusRemainingRef.current === null) {
        pausedVersusRemainingRef.current = Math.max(0, versusEndTimeRef.current - Date.now());
      }
    }
  }, [screen, versusGameOverStats]);

  const resumeVersusTimer = useCallback(() => {
    if (screen === 'game' && !versusGameOverStats && versusActiveTeamRef.current !== null) {
      if (pausedVersusRemainingRef.current !== null) {
        versusEndTimeRef.current = Date.now() + pausedVersusRemainingRef.current;
        pausedVersusRemainingRef.current = null;
      }
      if (!versusTimerRef.current && versusEndTimeRef.current > Date.now()) {
        const remaining = Math.max(0, versusEndTimeRef.current - Date.now());
        setVersusSelectionTimeLeftMs(remaining);
        versusTimerRef.current = window.setInterval(() => {
          const currentRemaining = Math.max(0, versusEndTimeRef.current - Date.now());
          setVersusSelectionTimeLeftMs(currentRemaining);
          if (currentRemaining > 0) {
            const sec = Math.ceil(currentRemaining / 1000);
            if (sec !== lastVersusSecRef.current) {
              lastVersusSecRef.current = sec;
              vibrateTick();
            }
          }
          if (currentRemaining <= 0) {
            lastVersusSecRef.current = null;
            if (versusTimerRef.current) {
              clearInterval(versusTimerRef.current);
              versusTimerRef.current = null;
            }
            const currentTeam = versusActiveTeamRef.current;
            if (currentTeam === 'blue') {
              versusBlueScoreRef.current = Math.max(0, versusBlueScoreRef.current - 1);
              setVersusBlueScore(versusBlueScoreRef.current);
            } else if (currentTeam === 'red') {
              versusRedScoreRef.current = Math.max(0, versusRedScoreRef.current - 1);
              setVersusRedScore(versusRedScoreRef.current);
            }
            triggerToast(`${currentTeam === 'blue' ? 'Blue' : 'Red'} timeout! (-1 point)`);
            vibrateBurst();
            setSelectedIndices([]);
            setVersusActiveTeam(null);
            versusActiveTeamRef.current = null;
            setVersusSelectionTimeLeftMs(5000);
          }
        }, 100);
      }
    }
  }, [screen, versusGameOverStats, triggerToast]);

  // Handle app focus, visibility, and native backgrounding
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      setIsAppActive(isVisible);
    };

    const handleWindowBlur = () => {
      setIsAppActive(false);
    };

    const handleWindowFocus = () => {
      if (document.visibilityState === 'visible') {
        setIsAppActive(true);
      }
    };

    const handlePageHide = () => {
      setIsAppActive(false);
    };

    const handlePageShow = () => {
      if (document.visibilityState === 'visible') {
        setIsAppActive(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);

    let removeCapListener: (() => void) | null = null;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      setIsAppActive(isActive);
    })
      .then((handle) => {
        removeCapListener = () => handle.remove();
      })
      .catch(() => {
        // Web / Node fallback
      });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      if (removeCapListener) removeCapListener();
    };
  }, []);

  // Master pause/resume effect for modals and app backgrounding
  useEffect(() => {
    if (isPaused) {
      if (gameMode === 'classic') {
        pauseTimer();
      } else if (gameMode === 'blitz') {
        pauseBlitzTimer();
      } else if (gameMode === 'versus') {
        pauseVersusTimer();
      }
    } else if (screen === 'game') {
      if (gameMode === 'classic' && !gameOverStats) {
        resumeTimer();
      } else if (gameMode === 'blitz' && !blitzGameOverStats) {
        resumeBlitzTimer();
      } else if (gameMode === 'versus' && !versusGameOverStats) {
        resumeVersusTimer();
      }
    }
  }, [
    isPaused,
    screen,
    gameMode,
    gameOverStats,
    blitzGameOverStats,
    versusGameOverStats,
    pauseTimer,
    resumeTimer,
    pauseBlitzTimer,
    resumeBlitzTimer,
    pauseVersusTimer,
    resumeVersusTimer,
  ]);

  const handleBackToMenu = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (blitzIntervalRef.current) {
      clearInterval(blitzIntervalRef.current);
      blitzIntervalRef.current = null;
    }
    if (versusTimerRef.current) {
      clearInterval(versusTimerRef.current);
      versusTimerRef.current = null;
    }
    lastVersusSecRef.current = null;
    lastBlitzSecRef.current = null;

    if (isGameFinished || gameOverStats !== null || blitzGameOverStats !== null || versusGameOverStats !== null) {
      setBoard([]);
      setDeck([]);
      setIsGameFinished(true);
      startTimeRef.current = 0;
      setElapsedMs(0);
      pauseStartTimeRef.current = null;
      pausedBlitzRemainingRef.current = null;
      pausedVersusRemainingRef.current = null;
      setVersusActiveTeam(null);
      versusActiveTeamRef.current = null;
    } else {
      // Game is still active! Record pause time so resuming from menu accounts for paused time
      if (gameMode === 'classic') {
        if (startTimeRef.current > 0 && pauseStartTimeRef.current === null) {
          pauseStartTimeRef.current = Date.now();
        }
      } else if (gameMode === 'blitz') {
        if (blitzEndTimeRef.current > 0 && pausedBlitzRemainingRef.current === null) {
          pausedBlitzRemainingRef.current = Math.max(0, blitzEndTimeRef.current - Date.now());
        }
      } else if (gameMode === 'versus') {
        if (versusActiveTeamRef.current !== null && versusEndTimeRef.current > 0 && pausedVersusRemainingRef.current === null) {
          pausedVersusRemainingRef.current = Math.max(0, versusEndTimeRef.current - Date.now());
        }
      }
    }
    if (gameMode === 'online_clash' || gameMode === 'online_race' || gameMode === 'online_blitz') {
      p2pClient.send({ type: 'LEAVE_MATCH' });
      p2pClient.cleanup();
      setOnlineLobbyMode(null);
      setOnlineRoomCode(null);
      setNetworkError(null);
    }
    if (onlineTimerRef.current) {
      clearInterval(onlineTimerRef.current);
      onlineTimerRef.current = null;
    }
    setGameOverStats(null);
    setBlitzGameOverStats(null);
    setVersusGameOverStats(null);
    setOnlineGameOverStats(null);
    setScreen('menu');
  };

  const processValidSet = useCallback(
    (matchedIndices: number[]) => {
      activeHintSetRef.current = null;
      revealedHintCardIdsRef.current.clear();
      setSelectedIndices([]);

      if (gameMode === 'online_race') {
        onlineSetsFoundRef.current += 1;
        p2pClient.send({
          type: 'RACE_PROGRESS',
          setsFound: onlineSetsFoundRef.current,
          cardsRemaining: Math.max(0, deck.length - 3),
        });
      } else if (gameMode === 'online_blitz') {
        onlineSetsFoundRef.current += 1;
        blitzEndTimeRef.current += 10000;
        const newTime = Math.max(0, blitzEndTimeRef.current - Date.now());
        setBlitzTimeLeftMs(newTime);
        vibrateSubtle();

        if (bonusTimeoutRef.current) clearTimeout(bonusTimeoutRef.current);
        setShowBonusTime(true);
        bonusTimeoutRef.current = window.setTimeout(() => {
          setShowBonusTime(false);
        }, 900);

        p2pClient.send({
          type: 'BLITZ_PROGRESS',
          setsFound: onlineSetsFoundRef.current,
          timeLeftMs: newTime,
          cardsRemaining: Math.max(0, deck.length - 3),
        });
      } else if (gameMode === 'blitz') {
        blitzSetsFoundRef.current += 1;
        blitzEndTimeRef.current += 10000;
        setBlitzTimeLeftMs(Math.max(0, blitzEndTimeRef.current - Date.now()));

        if (bonusTimeoutRef.current) clearTimeout(bonusTimeoutRef.current);
        setShowBonusTime(true);
        bonusTimeoutRef.current = window.setTimeout(() => {
          setShowBonusTime(false);
        }, 900);
      } else if (gameMode === 'versus') {
        if (versusTimerRef.current) {
          clearInterval(versusTimerRef.current);
          versusTimerRef.current = null;
        }
        const currentTeam = versusActiveTeamRef.current;
        if (currentTeam === 'blue') {
          versusBlueScoreRef.current += 1;
          setVersusBlueScore(versusBlueScoreRef.current);
        } else if (currentTeam === 'red') {
          versusRedScoreRef.current += 1;
          setVersusRedScore(versusRedScoreRef.current);
        }
        setVersusActiveTeam(null);
        versusActiveTeamRef.current = null;
        lastVersusSecRef.current = null;
        setVersusSelectionTimeLeftMs(5000);

        if (versusBlueScoreRef.current >= 14) {
          handleVersusGameOver('blue', 'First to 14 sets!');
          return;
        }
        if (versusRedScoreRef.current >= 14) {
          handleVersusGameOver('red', 'First to 14 sets!');
          return;
        }
      }

      const nextBoard = [...board];
      const nextDeck = [...deck];

      if (nextDeck.length >= 3) {
        const replacements = nextDeck.splice(0, 3);
        nextBoard[matchedIndices[0]] = replacements[0];
        nextBoard[matchedIndices[1]] = replacements[1];
        nextBoard[matchedIndices[2]] = replacements[2];
      } else {
        nextBoard[matchedIndices[0]] = null;
        nextBoard[matchedIndices[1]] = null;
        nextBoard[matchedIndices[2]] = null;
      }

      // Check if board has valid set
      const { board: finalBoard, deck: finalDeck, noSetsRemaining } = validateAndReshuffle(nextBoard, nextDeck, true);
      setBoard(finalBoard);
      setDeck(finalDeck);

      // Check game end
      const remainingCards = finalBoard.filter((c): c is Card => c !== null);
      if (noSetsRemaining || (finalDeck.length === 0 && !hasValidSet(remainingCards))) {
        if (gameMode === 'blitz') {
          handleBlitzGameOver('cleared');
        } else if (gameMode === 'online_blitz') {
          if (blitzIntervalRef.current) {
            clearInterval(blitzIntervalRef.current);
            blitzIntervalRef.current = null;
          }
          setIsGameFinished(true);
          p2pClient.send({
            type: 'BLITZ_DECK_CLEARED',
            setsFound: onlineSetsFoundRef.current,
          });
          setOnlineGameOverStats({
            winner: 'self',
            reason: 'You cleared the entire deck! Victory!',
            myScore: onlineSetsFoundRef.current,
            opponentScore: onlineOpponentProgressRef.current.setsFound,
          });
        } else if (gameMode === 'versus') {
          const b = versusBlueScoreRef.current;
          const r = versusRedScoreRef.current;
          const winner = b > r ? 'blue' : r > b ? 'red' : 'tie';
          const reason = b === r ? 'Equal sets found!' : `${winner.toUpperCase()} found more sets!`;
          handleVersusGameOver(winner, reason);
        } else if (gameMode === 'online_race') {
          setIsGameFinished(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          const finalTime = Date.now() - startTimeRef.current;
          const myRole = p2pClient.getRole() || 'host';
          p2pClient.send({
            type: 'RACE_FINISHED',
            winnerRole: myRole,
            finalTimeMs: finalTime,
            setsFound: onlineSetsFoundRef.current,
          });
          setOnlineGameOverStats({
            winner: 'self',
            reason: 'You cleared all sets first!',
            myScore: onlineSetsFoundRef.current,
            opponentScore: onlineOpponentProgress.setsFound,
          });
        } else {
          // Classic VICTORY!
          setIsGameFinished(true);
          if (timerRef.current) clearInterval(timerRef.current);
          const baseTime = Date.now() - startTimeRef.current;
          const totalHints = hintsUsedRef.current;
          const penaltySeconds = totalHints * 15;
          const finalTime = baseTime + penaltySeconds * 1000;
          const entry = saveScore(baseTime, totalHints);
          setGameOverStats({
            finalTime,
            baseTime,
            penaltySeconds,
            hintsUsed: totalHints,
            rank: entry ? entry.rank : null,
          });
        }
      }
    },
    [board, deck, gameMode, validateAndReshuffle, handleBlitzGameOver, handleVersusGameOver, onlineOpponentProgress]
  );

  const handleHintClick = () => {
    if (gameMode === 'blitz' || gameMode === 'versus' || isProcessingSetRef.current || isPaused) return;
    const activeCards = board.filter((c): c is Card => c !== null);
    if (activeCards.length === 0) return;

    let targetSet = activeHintSetRef.current;
    if (targetSet) {
      const boardIds = new Set(activeCards.map((c) => c.id));
      const allOnBoard = targetSet.every((c) => boardIds.has(c.id));
      if (!allOnBoard) targetSet = null;
    }

    if (!targetSet) {
      const validSets = findSets(activeCards);
      if (validSets.length === 0) {
        triggerToast('No valid sets on board!');
        return;
      }
      targetSet = validSets[0];
      activeHintSetRef.current = targetSet;
    }

    // Find card from target set that is not currently selected
    const unselectedCard = targetSet.find((targetCard) => {
      const idx = board.findIndex((b) => b?.id === targetCard.id);
      return idx !== -1 && !selectedIndices.includes(idx);
    });

    if (!unselectedCard) return;

    const unselectedIdx = board.findIndex((b) => b?.id === unselectedCard.id);
    if (unselectedIdx === -1) return;

    // Hint memory: only charge penalty if newly revealed
    if (!revealedHintCardIdsRef.current.has(unselectedCard.id)) {
      revealedHintCardIdsRef.current.add(unselectedCard.id);
      hintsUsedRef.current += 1;
      triggerToast('Hint used! (+15s penalty)');
    }

    const nextSelected = [...selectedIndices, unselectedIdx];
    setSelectedIndices(nextSelected);

    if (nextSelected.length === 3) {
      const c1 = board[nextSelected[0]]!;
      const c2 = board[nextSelected[1]]!;
      const c3 = board[nextSelected[2]]!;
      if (isSet(c1, c2, c3)) {
        isProcessingSetRef.current = true;
        window.setTimeout(() => {
          processValidSet(nextSelected);
          isProcessingSetRef.current = false;
        }, 400);
      }
    }
  };

  const handleCardClick = (index: number) => {
    if (isProcessingSetRef.current || isPaused) return;
    if (toastMessage) setToastMessage(null);
    const card = board[index];
    if (!card) return;

    if (gameMode === 'versus') {
      if (!versusActiveTeamRef.current) {
        return;
      }
    }

    if (gameMode === 'online_clash') {
      const myRole = p2pClient.getRole();
      if (!myRole) return;

      // 1. If unlocked: tap locks board and starts 5s timer!
      if (onlineLockRoleRef.current === null) {
        vibrateSubtle();
        const now = Date.now() + p2pClient.getClockOffset();
        onlineLockRoleRef.current = myRole;
        setOnlineLockRole(myRole);
        setSelectedIndices([index]);
        startOnlineSelectionTimer(myRole);

        if (myRole === 'host') {
          p2pClient.send({
            type: 'CLASH_LOCK_ACQUIRED',
            lockedBy: 'host',
            cardIndex: index,
          });
        } else {
          p2pClient.send({
            type: 'CLASH_LOCK_REQUEST',
            cardIndex: index,
            timestamp: now,
          });
        }
        return;
      }

      // 2. If locked by opponent: ignore tap
      if (onlineLockRoleRef.current !== myRole) {
        return;
      }

      // 3. Locked by me: select / deselect
      if (selectedIndices.includes(index)) {
        const next = selectedIndices.filter((i) => i !== index);
        setSelectedIndices(next);
        p2pClient.send({ type: 'CLASH_CARD_DESELECT', cardIndex: index });
        return;
      }

      const nextSelected = [...selectedIndices, index];
      p2pClient.send({ type: 'CLASH_CARD_SELECT', cardIndex: index });

      if (nextSelected.length < 3) {
        setSelectedIndices(nextSelected);
        return;
      }

      // 3 cards selected by me!
      const c1 = board[nextSelected[0]]!;
      const c2 = board[nextSelected[1]]!;
      const c3 = board[nextSelected[2]]!;

      if (isSet(c1, c2, c3)) {
        vibrateSubtle();
        if (onlineTimerRef.current) {
          clearInterval(onlineTimerRef.current);
          onlineTimerRef.current = null;
        }
        if (myRole === 'host') {
          resolveOnlineClashValidSet(nextSelected, 'host');
        }
        // If guest, Host will automatically resolve upon receiving the 3rd CLASH_CARD_SELECT
      } else {
        vibrateBurst();
        if (onlineTimerRef.current) {
          clearInterval(onlineTimerRef.current);
          onlineTimerRef.current = null;
        }
        triggerToast('Invalid set! (-1 point)');
        setSelectedIndices([]);
        setOnlineLockRole(null);
        onlineLockRoleRef.current = null;

        const newScores = applyScoreDelta(onlineScoresRef.current, myRole, -1);
        onlineScoresRef.current = newScores;
        setOnlineScores(newScores);

        p2pClient.send({
          type: 'CLASH_TIMEOUT',
          penalizedRole: myRole,
          scores: newScores,
        });
      }
      return;
    }

    if (selectedIndices.includes(index)) {
      // Deselect
      setSelectedIndices(selectedIndices.filter((i) => i !== index));
      return;
    }

    const nextSelected = [...selectedIndices, index];

    if (nextSelected.length < 3) {
      setSelectedIndices(nextSelected);
      return;
    }

    // Exactly 3 cards selected -> Check SET
    const c1 = board[nextSelected[0]]!;
    const c2 = board[nextSelected[1]]!;
    const c3 = board[nextSelected[2]]!;

    if (isSet(c1, c2, c3)) {
      processValidSet(nextSelected);
    } else {
      // Invalid match
      vibrateBurst();
      setSelectedIndices([]);
      if (gameMode === 'blitz') {
        // Sudden death disqualification in Blitz!
        handleBlitzGameOver('mistake');
      } else if (gameMode === 'online_blitz') {
        if (blitzIntervalRef.current) {
          clearInterval(blitzIntervalRef.current);
          blitzIntervalRef.current = null;
        }
        setIsGameFinished(true);
        p2pClient.send({
          type: 'BLITZ_ELIMINATED',
          reason: 'mistake',
          setsFound: onlineSetsFoundRef.current,
        });
        setOnlineGameOverStats({
          winner: 'opponent',
          reason: 'Invalid set selected! Knocked out!',
          myScore: onlineSetsFoundRef.current,
          opponentScore: onlineOpponentProgressRef.current.setsFound,
        });
      } else if (gameMode === 'versus') {
        if (versusTimerRef.current) {
          clearInterval(versusTimerRef.current);
          versusTimerRef.current = null;
        }
        const currentTeam = versusActiveTeamRef.current;
        if (currentTeam === 'blue') {
          versusBlueScoreRef.current = Math.max(0, versusBlueScoreRef.current - 1);
          setVersusBlueScore(versusBlueScoreRef.current);
        } else if (currentTeam === 'red') {
          versusRedScoreRef.current = Math.max(0, versusRedScoreRef.current - 1);
          setVersusRedScore(versusRedScoreRef.current);
        }
        triggerToast(`${currentTeam === 'blue' ? 'Blue' : 'Red'} invalid set! (-1 point)`);
        setVersusActiveTeam(null);
        versusActiveTeamRef.current = null;
        lastVersusSecRef.current = null;
        setVersusSelectionTimeLeftMs(5000);
      }
    }
  };

  const isGameActive =
    !isGameFinished &&
    board.some((c) => c !== null) &&
    gameOverStats === null &&
    blitzGameOverStats === null &&
    versusGameOverStats === null &&
    onlineGameOverStats === null &&
    gameMode !== 'online_clash' &&
    gameMode !== 'online_race' &&
    gameMode !== 'online_blitz';

  const myRole = p2pClient.getRole();
  const symmetric = getSymmetricScores(onlineScores, myRole || 'host');
  const activeOnlineTeam = onlineLockRole ? (onlineLockRole === myRole ? 'blue' : 'red') : null;

  return (
    <div className="app-container">
      {screen === 'menu' && (
        <MainMenu
          hasActiveGame={isGameActive}
          activeGameMode={gameMode}
          onResumeGame={() => setScreen('game')}
          onStartClassic={() => startClassicGame()}
          onStartBlitz={() => startBlitzGame()}
          onStartClashLocal={() => startVersusGame()}
          onOpenOnlineLobby={(mode) => {
            setOnlineLobbyMode(mode);
            setScreen('online_lobby');
          }}
          onOpenLeaderboard={() => {
            setLeaderboardInitialTab('classic');
            setIsLeaderboardOpen(true);
          }}
          onOpenHelp={() => setIsHelpOpen(true)}
        />
      )}

      {screen === 'online_lobby' && onlineLobbyMode && (
        <OnlineLobby
          mode={onlineLobbyMode}
          networkState={networkState}
          roomCode={onlineRoomCode}
          errorMessage={networkError}
          onCreateRoom={handleCreateOnlineRoom}
          onJoinRoom={handleJoinOnlineRoom}
          onCancel={handleCancelOnlineLobby}
        />
      )}

      {screen === 'game' && (
        <div className={`game-screen ${isPortrait ? 'portrait' : 'landscape'}`}>
          <GameBar
            mode={gameMode}
            onBackMenu={handleBackToMenu}
            elapsedMs={elapsedMs}
            blitzTimeLeftMs={blitzTimeLeftMs}
            versusBlueScore={gameMode === 'online_clash' ? symmetric.blueScore : versusBlueScore}
            versusRedScore={gameMode === 'online_clash' ? symmetric.redScore : versusRedScore}
            versusActiveTeam={gameMode === 'online_clash' ? activeOnlineTeam : versusActiveTeam}
            versusSelectionTimeLeftMs={
              gameMode === 'online_clash' ? onlineSelectionTimeLeftMs : versusSelectionTimeLeftMs
            }
            onlineOpponentSets={onlineOpponentProgress.setsFound}
            onlineOpponentCardsLeft={onlineOpponentProgress.cardsRemaining}
            onlineOpponentTimeLeftMs={onlineOpponentTimeLeftMs}
            cardsCount={deck.length + board.filter((c) => c !== null).length}
            onHint={handleHintClick}
            onHelp={() => setIsHelpOpen(true)}
            showBonusTime={showBonusTime}
          />

          {gameMode === 'versus' && isPortrait && (
            <VersusBuzzerPair
              activeTeam={versusActiveTeam}
              onBuzz={handleBuzzerPress}
              position="top"
            />
          )}

          {gameMode === 'versus' && !isPortrait && (
            <VersusBuzzer
              team="blue"
              activeTeam={versusActiveTeam}
              onBuzz={handleBuzzerPress}
              id="btn-buzz-blue-left"
              className="versus-buzzer-side versus-buzzer-left"
            />
          )}

          <div className="board-area">
            <div className={`board-grid ${isShimmering ? 'shimmering' : ''}`} id="board-grid">
              {board.map((card, idx) => (
                <CardTile
                  key={card ? card.id : `slot-${idx}`}
                  card={card}
                  index={idx}
                  isSelected={selectedIndices.includes(idx)}
                  selectedTeam={
                    gameMode === 'versus'
                      ? versusActiveTeam
                      : gameMode === 'online_clash'
                      ? activeOnlineTeam
                      : null
                  }
                  onClick={() => handleCardClick(idx)}
                />
              ))}
            </div>
          </div>

          {gameMode === 'versus' && !isPortrait && (
            <VersusBuzzer
              team="red"
              activeTeam={versusActiveTeam}
              onBuzz={handleBuzzerPress}
              id="btn-buzz-red-right"
              className="versus-buzzer-side versus-buzzer-right"
            />
          )}

          {gameMode === 'versus' && isPortrait && (
            <VersusBuzzerPair
              activeTeam={versusActiveTeam}
              onBuzz={handleBuzzerPress}
              position="bottom"
            />
          )}
        </div>
      )}

      {toastMessage && <div className="toast-notification">{toastMessage}</div>}

      {isReconnecting && (
        <div className="online-reconnecting-banner">
          ⚠️ Opponent connection lost... Reconnecting ({reconnectingSeconds}s)
        </div>
      )}

      {isHelpOpen && (
        <HelpModal
          onClose={() => setIsHelpOpen(false)}
          contextMode={screen === 'game' ? gameMode : null}
        />
      )}

      {gameOverStats && (
        <GameOverModal
          mode="classic"
          finalTimeMs={gameOverStats.finalTime}
          baseTimeMs={gameOverStats.baseTime}
          penaltySeconds={gameOverStats.penaltySeconds}
          hintsUsed={gameOverStats.hintsUsed}
          rank={gameOverStats.rank}
          onPlayAgain={() => startClassicGame()}
          onOpenLeaderboard={() => {
            setLeaderboardInitialTab('classic');
            setIsLeaderboardOpen(true);
          }}
          onBackMenu={handleBackToMenu}
        />
      )}

      {blitzGameOverStats && (
        <GameOverModal
          mode="blitz"
          blitzSetsFound={blitzGameOverStats.setsFound}
          blitzTimeLeftMs={blitzGameOverStats.timeLeftMs}
          blitzEndReason={blitzGameOverStats.endReason}
          rank={blitzGameOverStats.rank}
          onPlayAgain={() => startBlitzGame()}
          onOpenLeaderboard={() => {
            setLeaderboardInitialTab('blitz');
            setIsLeaderboardOpen(true);
          }}
          onBackMenu={handleBackToMenu}
        />
      )}

      {versusGameOverStats && (
        <GameOverModal
          mode="versus"
          versusWinner={versusGameOverStats.winner}
          versusBlueScore={versusGameOverStats.blueScore}
          versusRedScore={versusGameOverStats.redScore}
          versusReason={versusGameOverStats.reason}
          rank={null}
          onPlayAgain={() => startVersusGame()}
          onBackMenu={handleBackToMenu}
        />
      )}

      {onlineGameOverStats && (
        <GameOverModal
          mode={gameMode}
          onlineWinner={onlineGameOverStats.winner}
          onlineMyScore={onlineGameOverStats.myScore}
          onlineOpponentScore={onlineGameOverStats.opponentScore}
          onlineReason={onlineGameOverStats.reason}
          rematchRequestedByPeer={rematchRequestedByPeer}
          rematchSent={rematchSent}
          rank={null}
          onPlayAgain={handleRematchClick}
          onBackMenu={handleBackToMenu}
        />
      )}

      {isLeaderboardOpen && (
        <LeaderboardModal
          onClose={() => setIsLeaderboardOpen(false)}
          initialTab={leaderboardInitialTab}
          highlightRank={gameOverStats ? gameOverStats.rank : null}
          highlightBlitzId={lastBlitzEntryId}
        />
      )}
    </div>
  );
};
